
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMovieContext } from "./useMovieContext";

/**
 * Custom hook to manage view mode (grid/list) with URL persistence
 */
export const useViewMode = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { updateViewMode } = useMovieContext();
  
  // Initialize viewMode from URL or default to grid with proper typing
  const urlView = searchParams.get('view');
  const initialView: "grid" | "list" = (urlView === 'list' || urlView === 'grid') 
    ? urlView 
    : "grid";
  
  const [viewMode, setViewModeState] = useState<"grid" | "list">(initialView);
  
  // FIXED: Remove dependency on viewMode to prevent infinite loops
  useEffect(() => {
    const urlView = searchParams.get('view');
    const validView: "grid" | "list" = (urlView === 'list' || urlView === 'grid') 
      ? urlView 
      : "grid";
    
    if (validView !== viewMode) {
      setViewModeState(validView);
      updateViewMode(validView);
    }
  }, [searchParams, updateViewMode]); // Remove viewMode dependency
  
  // Handle view mode change with URL updates
  const handleViewModeChange = useCallback((mode: "grid" | "list") => {
    // Update local state
    setViewModeState(mode);
    
    // Update context for immediate UI response
    updateViewMode(mode);
    
    // Update URL with new view mode
    const newSearchParams = new URLSearchParams(searchParams);
    if (mode === "grid") {
      // Remove view param for default value to keep URL clean
      newSearchParams.delete('view');
    } else {
      newSearchParams.set('view', mode);
    }
    setSearchParams(newSearchParams, { replace: true });
  }, [searchParams, setSearchParams, updateViewMode]);
  
  return {
    viewMode,
    handleViewModeChange
  };
};
